/**
 * ColorPicker — Compact Inline Color Picker
 *
 * Reusable color picker embedded inside the PropertiesPanel.
 * Shows theme colors, recent colors, hex input, and native color wheel.
 * Glass-morphism dark design matching the editor aesthetic.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Pipette, X } from 'lucide-react';

const RECENT_COLORS_KEY = 'editor_recent_colors';
const MAX_RECENT = 8;

/** Read recent colors from localStorage */
function getRecentColors() {
  try {
    const stored = localStorage.getItem(RECENT_COLORS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed.slice(0, MAX_RECENT);
    }
  } catch {
    // ignore parse errors
  }
  return [];
}

/** Push a color to the front of the recent list */
function pushRecentColor(color) {
  if (!color || typeof color !== 'string') return;
  const normalized = color.trim().toLowerCase();
  if (!normalized) return;
  const recent = getRecentColors().filter(c => c.toLowerCase() !== normalized);
  recent.unshift(normalized);
  const trimmed = recent.slice(0, MAX_RECENT);
  try {
    localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(trimmed));
  } catch {
    // storage full — ignore
  }
}

/** Validate a hex color string (3, 4, 6, or 8 hex chars after #) */
function isValidHex(str) {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(str);
}

/** Swatch circle — shared between theme and recent rows */
function Swatch({ color, size = 22, onClick, isActive, title }) {
  const [hovered, setHovered] = useState(false);

  const isTransparent = !color;
  const borderColor = isActive
    ? '#60a5fa'
    : hovered
      ? 'rgba(255,255,255,0.5)'
      : 'rgba(255,255,255,0.15)';

  return (
    <button
      type="button"
      title={title || color || 'None'}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: `2px solid ${borderColor}`,
        background: isTransparent
          ? 'repeating-conic-gradient(rgba(255,255,255,0.15) 0% 25%, transparent 0% 50%) 0 0 / 8px 8px'
          : color,
        cursor: 'pointer',
        padding: 0,
        flexShrink: 0,
        transition: 'border-color 0.15s ease, transform 0.15s ease',
        transform: hovered ? 'scale(1.15)' : 'scale(1)',
        outline: 'none',
        boxShadow: isActive ? '0 0 0 2px rgba(96,165,250,0.3)' : 'none',
      }}
    />
  );
}

const ColorPicker = ({ value, onChange, themeColors, label }) => {
  const [hexInput, setHexInput] = useState(value || '');
  const [recentColors, setRecentColors] = useState(getRecentColors);
  const [hexError, setHexError] = useState(false);
  const nativeInputRef = useRef(null);

  // Sync hex input when external value changes
  useEffect(() => {
    setHexInput(value || '');
    setHexError(false);
  }, [value]);

  const applyColor = useCallback((color) => {
    onChange(color);
    if (color) {
      pushRecentColor(color);
      setRecentColors(getRecentColors());
    }
  }, [onChange]);

  const handleHexChange = useCallback((e) => {
    let raw = e.target.value;
    setHexInput(raw);

    // Auto-prepend # if user types bare hex digits
    if (raw && !raw.startsWith('#')) {
      raw = '#' + raw;
    }

    if (isValidHex(raw)) {
      setHexError(false);
      applyColor(raw);
    } else if (raw === '' || raw === '#') {
      setHexError(false);
    } else {
      setHexError(true);
    }
  }, [applyColor]);

  const handleHexBlur = useCallback(() => {
    // On blur, reset to the current value if input is invalid
    if (hexError || (!hexInput && value)) {
      setHexInput(value || '');
      setHexError(false);
    }
  }, [hexError, hexInput, value]);

  const handleHexKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }, []);

  const handleNativeChange = useCallback((e) => {
    const color = e.target.value;
    setHexInput(color);
    setHexError(false);
    applyColor(color);
  }, [applyColor]);

  const handleClear = useCallback(() => {
    setHexInput('');
    setHexError(false);
    onChange('');
  }, [onChange]);

  const openNativePicker = useCallback(() => {
    nativeInputRef.current?.click();
  }, []);

  // Build theme color entries
  const themeEntries = themeColors
    ? Object.entries(themeColors).filter(([, v]) => typeof v === 'string' && v)
    : [];

  return (
    <div style={{
      background: 'rgba(15, 23, 42, 0.95)',
      backdropFilter: 'blur(12px)',
      borderRadius: 12,
      border: '1px solid rgba(255,255,255,0.08)',
      padding: '10px 12px',
      maxWidth: 200,
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    }}>

      {/* Label */}
      {label && (
        <div style={{
          fontSize: 10,
          fontWeight: 600,
          color: 'rgba(255,255,255,0.5)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: 8,
        }}>
          {label}
        </div>
      )}

      {/* Current color swatch + hex input row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 10,
      }}>
        <Swatch
          color={value || ''}
          size={28}
          isActive
          onClick={openNativePicker}
          title="Click for color wheel"
        />
        <input
          type="text"
          value={hexInput}
          onChange={handleHexChange}
          onBlur={handleHexBlur}
          onKeyDown={handleHexKeyDown}
          placeholder="#000000"
          spellCheck={false}
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
            color: hexError ? '#fca5a5' : '#e2e8f0',
            background: 'rgba(255,255,255,0.06)',
            border: `1px solid ${hexError ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 6,
            padding: '5px 8px',
            outline: 'none',
            transition: 'border-color 0.15s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = hexError ? 'rgba(239,68,68,0.7)' : 'rgba(96,165,250,0.5)';
          }}
          onBlurCapture={(e) => {
            e.target.style.borderColor = hexError ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)';
          }}
        />
      </div>

      {/* Theme colors */}
      {themeEntries.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{
            fontSize: 9,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.35)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 5,
          }}>
            Theme
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 5,
          }}>
            {themeEntries.map(([key, color]) => (
              <Swatch
                key={key}
                color={color}
                size={20}
                isActive={value && value.toLowerCase() === color.toLowerCase()}
                onClick={() => applyColor(color)}
                title={`${key}: ${color}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recent colors */}
      {recentColors.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{
            fontSize: 9,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.35)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 5,
          }}>
            Recent
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 5,
          }}>
            {recentColors.map((color, idx) => (
              <Swatch
                key={`${color}-${idx}`}
                color={color}
                size={18}
                isActive={value && value.toLowerCase() === color.toLowerCase()}
                onClick={() => applyColor(color)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Bottom row: Custom button + Transparent button */}
      <div style={{
        display: 'flex',
        gap: 6,
        marginTop: 2,
      }}>
        {/* Custom color button + hidden native picker */}
        <button
          type="button"
          onClick={openNativePicker}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            padding: '5px 0',
            fontSize: 11,
            fontWeight: 500,
            color: '#93c5fd',
            background: 'rgba(96,165,250,0.08)',
            border: '1px solid rgba(96,165,250,0.15)',
            borderRadius: 6,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(96,165,250,0.15)';
            e.currentTarget.style.borderColor = 'rgba(96,165,250,0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(96,165,250,0.08)';
            e.currentTarget.style.borderColor = 'rgba(96,165,250,0.15)';
          }}
        >
          <Pipette size={12} />
          Custom
        </button>

        {/* Transparent / none button */}
        <button
          type="button"
          onClick={handleClear}
          title="Remove color (transparent)"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            padding: '5px 10px',
            fontSize: 11,
            fontWeight: 500,
            color: value ? '#f87171' : 'rgba(255,255,255,0.3)',
            background: value ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${value ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: 6,
            cursor: value ? 'pointer' : 'default',
            transition: 'all 0.15s ease',
            opacity: value ? 1 : 0.5,
          }}
          onMouseEnter={(e) => {
            if (value) {
              e.currentTarget.style.background = 'rgba(239,68,68,0.15)';
              e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = value ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.04)';
            e.currentTarget.style.borderColor = value ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.08)';
          }}
        >
          <X size={12} />
          None
        </button>

        {/* Hidden native color input */}
        <input
          ref={nativeInputRef}
          type="color"
          value={value && isValidHex(value) ? value : '#000000'}
          onChange={handleNativeChange}
          style={{
            position: 'absolute',
            width: 0,
            height: 0,
            overflow: 'hidden',
            opacity: 0,
            pointerEvents: 'none',
          }}
          tabIndex={-1}
        />
      </div>
    </div>
  );
};

export default ColorPicker;
